import { Cancel, CheckCircle } from "@mui/icons-material";
import { Box, Button, Chip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer } from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MyButton from "../../components/MyButton";
import PageHeader from "../../components/PageHeader";
import { REP_TYPES } from "../../constants";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useReps } from "../../hooks/useReps";
import routes from "../../routes";
import { getInactiveRowClassName, inactiveRowStyles } from "../../util/DataGridUtil";
import { disableSortingAndMenu } from "../../util/FilterOperatorsUtil";
import RepsFilter from "./RepsFilter";
import SalesTerritoriesModal from "./SalesTerritoriesModal";

const RepsTable = () => {
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });

    const [filter, setFilter] = useState({
        businessUnitCode: "",
        userTypeId: "",
        activated: "",
        salesRegion: "",
        salesTerritory: "",
        searchString: "",
    });

    const [columnWidths, setColumnWidths] = useLocalStorage("repsTableColumnWidths", {});

    const [currentUser, _updateCurrentUser] = useCurrentUser(true, (user) => {
        if (user) {
            if (user.businessUnitCode) {
                setFilter((prevState) => ({
                    ...prevState,
                    businessUnitCode: user.businessUnitCode,
                }));
            }
            if (user.salesRegion) {
                setFilter((prevState) => ({
                    ...prevState,
                    salesRegion: user.salesRegion,
                }));
            }
        }
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (!routes.find((route) => route.key === "reps").visibleRepTypes.includes(currentUser.repType.id)) {
            navigate(REP_TYPES[currentUser.repType.id].defaultRoute);
        }
    }, [currentUser, navigate]);

    const [salesTerritoriesModalData, setSalesTerritoriesModalData] = useState({open: false, data: []});

    const salesTerritoriesRef = useRef();

    const columns = useMemo(() => {
        let header = [
            {field: "id", headerName: "ID", width: columnWidths.id ?? 70},
            {
                field: "userType",
                headerName: "USER TYPE",
                width: columnWidths.userType ?? 250,
                renderCell: (params) => {
                    return params.value ? params.value.displayName : "NA";
                },
            },
            {
                field: "groups",
                headerName: "GROUPS",
                width: columnWidths.groups ?? 260,
                renderCell: (params) => {
                    const groups = params.value || [];
                    if (groups.length === 0) {
                        return "";
                    }
                    return (
                        <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center" height="100%">
                            {groups.map((g) => (
                                <Chip key={g.id} label={g.name} size="small" variant="outlined" />
                            ))}
                        </Box>
                    );
                },
            },
            {field: "businessUnitCode", headerName: "BUSINESS UNIT", width: columnWidths.businessUnitCode ?? 130},
            {field: "login", headerName: "LOGIN", width: columnWidths.login ?? 250},
            {field: "firstName", headerName: "FIRST NAME", width: columnWidths.firstName ?? 100},
            {field: "lastName", headerName: "LAST NAME", width: columnWidths.lastName ?? 100},
            {field: "email", headerName: "EMAIL", width: columnWidths.email ?? 200},
            {field: "salesRegion", headerName: "REGION", width: columnWidths.salesRegion ?? 80},
            {
                field: "salesTerritories",
                headerName: "TERRITORIES",
                width: columnWidths.salesTerritories ?? 200,
                renderCell: (params) => {
                    return params.value.length === 0 ? (
                        ""
                    ) : (
                        <Button
                            onClick={(event) => {
                                event.stopPropagation();
                                setSalesTerritoriesModalData({
                                    open: true,
                                    data: params.value,
                                });
                                salesTerritoriesRef.current.setSalesTerritory(
                                    params.value.length > 0 ? params.value[0] : ""
                                );
                            }}
                        >
                            {params.value ? params.value.join(", ") : "NA"}
                        </Button>
                    );
                },
            },
            {field: "shiptoId", headerName: "SHIPTO", width: columnWidths.email ?? 200},
            {field: "billtoId", headerName: "BILLTO", width: columnWidths.email ?? 200},
            {
                field: "retailLocation",
                headerName: "RETAIL LOCATION",
                width: columnWidths.retailLocation ?? 200,
                renderCell: (params) => {
                    return params.value ? params.value.displayName : "";
                },
            },
            {
                field: "adminPortalAccess",
                headerName: "ADMIN PORTAL ACCESS",
                width: columnWidths.adminPortalAccess ?? 180,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
            },
            {
                field: "lastLoginDate",
                headerName: "LAST LOGIN DATE",
                width: columnWidths.lastLoginDate ?? 170,
                valueFormatter: (value) => {
                    return value ? new Date(value).toLocaleString() : "Never";
                },
            },
            {
                field: "activated",
                headerName: "ACTIVATED",
                width: columnWidths.activated ?? 100,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        {params.value ? <CheckCircle color="success" /> : <Cancel color="error" />}
                    </Box>
                ),
            },
        ];
        disableSortingAndMenu(header);
        return header;
    }, []);

    const [reps, totalRowCount, refetchReps] = useReps(
        filter.businessUnitCode,
        filter.activated,
        filter.salesRegion,
        filter.salesTerritory,
        paginationModel.page,
        paginationModel.pageSize,
        true,
        filter.searchString,
        undefined,
        filter.userTypeId
    );

    const handleColumnResize = (params) => {
        setColumnWidths((prevWidths) => ({
            ...prevWidths,
            [params.colDef.field]: params.width,
        }));
    };

    function handleRowClick(params) {
        navigate(`/users/${params.row.id}`);
    }

    function handleCloseModal() {
        setSalesTerritoriesModalData((prev) => ({
            ...prev,
            open: false,
        }));
    }

    const filterChangeFunctions = useMemo(() => ({
        businessUnitCode: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                businessUnitCode: value,
                salesRegion: "",
                salesTerritory: "",
            }));
        },
        userTypeId: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                userTypeId: value,
            }));
        },
        activated: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                activated: value,
            }));
        },
        salesRegion: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                salesRegion: value,
                salesTerritory: "",
            }));
        },
        salesTerritory: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                salesTerritory: value,
            }));
        },
        searchString: (value) => {
            setFilter((prevState) => ({
                ...prevState,
                searchString: value,
            }));
        },
    }), []);
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
            </GridToolbarContainer>
        );
    }

    function renderControlPanel() {
        return (
            <Box pt={1}>
                <Grid
                    container
                    spacing={1}
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                    }}
                >
                    <Grid item xs={10}>
                        <RepsFilter
                            filter={filter}
                            filterChangeFunctions={filterChangeFunctions}
                            setFilter={setFilter}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        sx={{
                            display: "flex",
                            justifyContent: "right",
                            alignItems: "flex-end",
                        }}
                    >
                        <MyButton variant="contained" color="primary" onClick={() => navigate("/users/create")}>
                            + New User
                        </MyButton>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/*<Topbar title="Manage Reps"/>*/}
                <PageHeader title="Manage Users" children={renderControlPanel()} />

                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    <DataGrid
                        columns={columns}
                        rows={reps}
                        paginationMode="server"
                        rowCount={totalRowCount}
                        pageSizeOptions={[10, 20, 50]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        density="compact"
                        slots={{toolbar: CustomToolbar}}
                        slotProps={{
                            toolbar: {
                                csvOptions: {disableToolbarButton: true},
                                printOptions: {disableToolbarButton: true},
                                showQuickFilter: false,
                            },
                        }}
                        onRowClick={handleRowClick}
                        disableRowSelectionOnClick={true}
                        onColumnWidthChange={handleColumnResize}
                        getRowClassName={getInactiveRowClassName("activated")}
                        sx={{
                            height: "100%",
                            position: "absolute",
                            width: "100%",
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-row": {
                                cursor: "pointer",
                            },
                            "& .MuiDataGrid-footerContainer": {
                                minHeight: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .MuiTablePagination-root": {
                                overflow: "hidden",
                            },
                            "& .MuiTablePagination-toolbar": {
                                minHeight: "40px",
                                height: "40px",
                            },
                            ...inactiveRowStyles,
                            // fontSize: '1rem',
                        }}
                    />
                </Box>
            </Box>
            <SalesTerritoriesModal
                ref={salesTerritoriesRef}
                payload={salesTerritoriesModalData}
                closeModal={handleCloseModal}
            />
        </>
    );
};

export default RepsTable;
